import React, { useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { UploadError } from "@ff-os/shared-react/components/file-uploader/FileUploader";
import { UploadMethodsWindow } from "@ff-os/shared-react/components/upload-methods/UploadMethodsWindow";
import { usePhotoPrintsUploadMethods } from "../../../createPhotoPrintsUploadMethods";
import { useMemoizedGroup } from "../../../memoizations/_redux-memoization";
import { uploadImages } from "../../../thunks/uploadImages";
import {
    incrementWithScheduledReset,
    updateWithScheduledReset
} from "../../../thunks/withScheduledReset";
import { MultipleAccessoriesWindow } from "../../photo-print-accessories/MultipleAccessoriesWindow";
import { EmptyPhotoPrintsGroup } from "./EmptyPhotoPrintsGroup";
import { NotEmptyPhotoPrintsGroup } from "./NotEmptyPhotoPrintsGroup";
import { PhotoPrintGroupPanel } from "./PhotoPrintGroupPanel";
import { PhotoPrintsGroupDropzone } from "./PhotoPrintsGroupDropzone";

const PhotoPrintsGroupComponent = ({
    groupId,
    className
}: PhotoPrintsGroupProps) => {

    const dispatch = useDispatch();

    const { isEmpty } = useMemoizedGroup(groupId);

    const [isUploading, setIsUploading] = useState(false);

    const addPictures = useCallback((files: File[]) => {
        setIsUploading(true);
        dispatch(uploadImages(files, groupId));
    }, [dispatch, groupId]);

    const onError = useCallback((errors: UploadError[]) => {
        dispatch(incrementWithScheduledReset(groupId, 'failedUploadsCount', errors.length));
        dispatch(updateWithScheduledReset({ id: groupId, key: 'failedUploadsError', value: errors }));
    }, [dispatch, groupId]);

    const [isOpen, setIsOpen] = useState(false);
    const [isAccessoriesOpen, setIsAccessoriesOpen] = useState(false);
    const [activeAccessory, setActiveAccessory] = useState("");

    const [dragDisabled, setDragDisabled] = useState(false);
    const [dragging, setDragging] = useState(false);

    const uploadMethodsProps = useMemo(() => ({
        externalUpload: { groupId },
        dropzone: { onChange: addPictures, onError  }
    }), [groupId, addPictures, onError]);

    const uploadMethods = usePhotoPrintsUploadMethods(uploadMethodsProps);

    const openUploadMethods = () => setIsOpen(true);

    const openAccessories = (accessoryId: string) => {
        setActiveAccessory(accessoryId);
        setIsAccessoriesOpen(true);
    };

    const closeAccessories = () => {
        setActiveAccessory("");
        setIsAccessoriesOpen(false);
    };

    return (
        <PhotoPrintsGroupDropzone
            className={className}
            groupId={groupId}
            disabled={dragDisabled}
            onDragStart={() => setDragging(true)}
            onDragEnd={() => setDragging(false)}>
            <>
                <PhotoPrintGroupPanel
                    dragging={dragging}
                    browse={openUploadMethods}
                    groupId={groupId}
                    openAccessories={openAccessories}
                    isUploading={isUploading} >
                    {isEmpty ? (
                        <EmptyPhotoPrintsGroup openUploadMethods={openUploadMethods} />
                    ) : (
                        <NotEmptyPhotoPrintsGroup groupId={groupId} setDragDisabled={setDragDisabled} />
                    )}
                </PhotoPrintGroupPanel>
                <UploadMethodsWindow
                    methods={uploadMethods}
                    isOpen={isOpen}
                    close={() => setIsOpen(false)} />
                {isAccessoriesOpen && (
                    <MultipleAccessoriesWindow
                        isOpen={isAccessoriesOpen}
                        activeAccessory={activeAccessory}
                        groupId={groupId}
                        close={closeAccessories} />
                )}
            </>
        </PhotoPrintsGroupDropzone>
    );
};

type PhotoPrintsGroupProps = {
    groupId: string;
    className?: string;
};

export const PhotoPrintsGroup = React.memo(PhotoPrintsGroupComponent);